import React, { useState } from 'react';
import axios from 'axios';
import { Eye, EyeOff, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { PasswordStrengthMeter } from '../PasswordStrengthMeter';

const API = process.env.REACT_APP_BACKEND_URL;
const errText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(' ');
  return d?.message || fallback;
};

// Change own password: current + new + confirm. Server signs out other devices and returns a fresh token for this one.
export const ChangePasswordCard = ({ token, onNewToken, onChanged }) => {
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const mismatch = form.confirm_password && form.new_password !== form.confirm_password;

  const submit = async (e) => {
    e.preventDefault();
    if (mismatch) return toast.error('New passwords do not match.');
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/api/auth/change-password`, form, { headers: { Authorization: `Bearer ${token}` } });
      onNewToken?.(data.access_token);
      setForm({ current_password: '', new_password: '', confirm_password: '' });
      toast.success(data.message);
      onChanged?.();
    } catch (err) {
      toast.error(errText(err, 'Could not change password'));
    } finally {
      setSaving(false);
    }
  };

  const field = (k, label, testId, extra) => (
    <div>
      <Label htmlFor={testId}>{label}</Label>
      <Input id={testId} type={show ? 'text' : 'password'} value={form[k]} onChange={set(k)} required autoComplete={k === 'current_password' ? 'current-password' : 'new-password'} className="mt-1" data-testid={testId} />
      {extra}
    </div>
  );

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
      <div className="flex items-center justify-between pr-8">
        <h3 className="font-semibold flex items-center gap-2"><KeyRound className="h-4 w-4 text-amber-600" /> Change Password</h3>
        <button type="button" onClick={() => setShow(!show)} className="text-xs text-neutral-500 hover:text-neutral-800 inline-flex items-center gap-1" data-testid="change-password-toggle-visibility">
          {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}{show ? 'Hide' : 'Show'}
        </button>
      </div>
      {field('current_password', 'Current password', 'current-password-input')}
      {field('new_password', 'New password', 'new-password-input', <PasswordStrengthMeter password={form.new_password} />)}
      {field('confirm_password', 'Confirm new password', 'confirm-new-password-input', mismatch && <p className="text-xs text-red-600 mt-1" data-testid="password-mismatch">Passwords do not match</p>)}
      <p className="text-xs text-neutral-500">At least 10 characters. A few unrelated words make a strong, memorable passphrase. Other devices will be signed out.</p>
      <Button type="submit" disabled={saving || mismatch} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="change-password-submit">
        {saving ? 'Updating…' : 'Update Password'}
      </Button>
    </form>
  );
};

export { errText };
